import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Heart, Shield, Clock, Globe, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data.filter(s => s.slug !== 'consultation'));
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  };

  const stagger = {
    animate: { transition: { staggerChildren: 0.1 } }
  };

  const features = [
    {
      icon: Heart,
      title: 'Trauma-Informed Care',
      description: 'Specialised support for those who have experienced trauma, abuse, and long-standing emotional distress in a safe, containing space.'
    },
    {
      icon: Shield,
      title: 'Complete Confidentiality',
      description: 'GDPR compliant practice. Your privacy and the confidentiality of our work together are of utmost importance.'
    },
    {
      icon: Globe,
      title: 'Multilingual Therapy',
      description: 'Therapy available in English, Portuguese, Spanish, and French. Choose the language that feels most emotionally genuine to you.'
    }
  ];

  const specialisations = [
    'Trauma & Childhood Trauma',
    'Domestic & Relational Abuse',
    'Coercive Control',
    'Racial Trauma & Discrimination',
    'Borderline Personality Dynamics',
    'Migration & Identity',
    'Fear of Abandonment',
    'Difficulties with Trust & Intimacy'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <motion.div
              className="lg:col-span-7 space-y-8"
              initial="initial"
              animate="animate"
              variants={stagger}
            >
              <motion.p
                variants={fadeInUp}
                className="text-sm font-medium tracking-wide uppercase text-accent"
              >
                Psychotherapy in London & Online
              </motion.p>
              
              <motion.h1
                variants={fadeInUp}
                className="font-heading text-5xl md:text-7xl font-light tracking-tight leading-[0.95] text-foreground"
              >
                A space for<br />
                <span className="text-primary">reflection</span><br />
                and healing
              </motion.h1>
              
              <motion.p
                variants={fadeInUp}
                className="text-lg md:text-xl font-normal leading-relaxed text-muted-foreground max-w-xl"
              >
                I work with adults navigating trauma, oppressive or abusive relationships, 
                and long-standing emotional distress. Together, we explore how earlier 
                experiences continue to shape your present life.
              </motion.p>
              
              <motion.div variants={fadeInUp} className="flex flex-wrap gap-4 pt-4">
                <Button
                  size="lg"
                  className="rounded-full px-8 py-6 font-medium bg-primary hover:bg-primary/90 hover:scale-[1.02] transition-all duration-300"
                  onClick={() => navigate('/book')}
                  data-testid="hero-book-btn"
                >
                  Book a Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8 py-6 font-medium border-2 border-primary text-primary hover:bg-primary/5 hover:scale-[1.02] transition-all duration-300"
                  onClick={() => navigate('/about')}
                  data-testid="hero-learn-more-btn"
                >
                  Learn More
                </Button>
              </motion.div>

              <motion.div variants={fadeInUp} className="pt-4">
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium">Kátia Aisha Belo Cardoso</span> · BACP Registered (MBACP) · #410538
                </p>
              </motion.div>
            </motion.div>

            <motion.div
              className="lg:col-span-5 hidden lg:block"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-accent/20 rounded-3xl transform rotate-3" />
                <img
                  src="https://customer-assets.emergentagent.com/job_kbc-therapy-platform/artifacts/eork56sw_Katia%20front%20facing.jpeg"
                  alt="Kátia Aisha Belo Cardoso - Psychotherapist"
                  className="relative rounded-2xl shadow-2xl object-cover w-full aspect-[4/5]"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-secondary/30" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight mb-4">
              My Approach
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Psychotherapy informed by trauma awareness and an understanding 
              of how culture, language, race, and identity shape emotional experience
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card rounded-2xl p-8 shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)] hover:shadow-[0_20px_40px_-12px_rgba(42,90,82,0.12)] transition-all duration-500"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                  <feature.icon className="h-6 w-6 text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-2xl font-normal mb-3">{feature.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialisations Section */}
      <section className="py-24" data-testid="specialisations-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight mb-6">
                Areas of<br />Specialisation
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                I have experience working in NHS and community mental health settings, 
                supporting individuals through complex emotional difficulties. You do not 
                need a formal diagnosis to begin therapy.
              </p>
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => navigate('/about')}
              >
                More About My Work
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex flex-wrap gap-3"
            >
              {specialisations.map((item, index) => (
                <motion.span
                  key={item}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="px-5 py-3 bg-card rounded-full text-sm font-medium shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)] hover:shadow-[0_8px_30px_-6px_rgba(42,90,82,0.15)] transition-all duration-300"
                >
                  {item}
                </motion.span>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-secondary/30" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            className="mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight mb-4">
              Services
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Long-term, in-depth therapeutic work tailored to your individual needs
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={`/services/${service.slug}`}
                  className="group block bg-card rounded-2xl overflow-hidden shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)] hover:shadow-[0_20px_40px_-12px_rgba(42,90,82,0.12)] transition-all duration-500 h-full"
                  data-testid={`service-card-${service.slug}`}
                >
                  <div className="h-32 bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center">
                    <span className="font-heading text-5xl text-primary/20 group-hover:scale-110 transition-transform duration-500">
                      {service.name.charAt(0)}
                    </span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading text-xl font-normal mb-2 group-hover:text-primary transition-colors">
                      {service.name}
                    </h3>
                    <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
                      {service.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">
                        {service.duration_minutes} min · £{service.price}
                      </span>
                      <ChevronRight className="h-5 w-5 text-primary group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-24" data-testid="location-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)]"
            >
              <h3 className="font-heading text-2xl mb-4">In-Person Sessions</h3>
              <p className="text-muted-foreground mb-4">
                I offer face-to-face sessions at two locations in London:
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary" />
                  Central London
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary" />
                  Clapham, South London
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-card rounded-2xl p-8 shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)]"
            >
              <h3 className="font-heading text-2xl mb-4">Online Sessions</h3>
              <p className="text-muted-foreground mb-4">
                Online therapeutic support is available for:
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary" />
                  Clients based in the UK
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary" />
                  International clients seeking in-depth therapeutic work
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl md:text-5xl font-light text-primary-foreground mb-4">
              Begin Your Journey
            </h2>
            <p className="text-lg text-primary-foreground/80 max-w-2xl mx-auto mb-8">
              If something here resonates, I invite you to get in touch. 
              The initial consultation is a chance to meet, ask questions, 
              and see whether we are a good fit to work together.
            </p>
            <Button
              size="lg"
              variant="secondary"
              className="rounded-full px-8 py-6 font-medium hover:scale-[1.02] transition-all duration-300"
              onClick={() => navigate('/book')}
              data-testid="cta-book-btn"
            >
              Book Initial Consultation · £50
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
